// ============================================================
// <Fortschritt> — das Band.
//
// Zwei Betriebsarten:
//   bestimmt   (wert/gesamt) → Balken plus Schrittzeile "Seite 12 von 40 gelesen"
//   unbestimmt (schritte)    → wanderndes Band plus wachsende Schrittliste
//
// Die Kurve ist nicht nach Gefuehl gewaehlt, sondern nach Harrison u.a.
// (UIST 2007): am Anfang untertreiben, zum Ende hin beschleunigen. Die
// Gewinner der Studie waren exponentiell mit dem schnellsten Zuwachs gegen
// Ende; die Verlierer hatten Pausen kurz vor dem Abschluss. Deshalb wird der
// rohe Anteil mit ^1.6 gezeichnet: bei 50 % steht der Balken auf 33 % und
// holt zum Schluss sichtbar auf. Der Balken ist also bewusst KEIN Messwert,
// sondern eine Wahrnehmungskurve — die harten Zahlen stehen in der Schrittzeile.
//
// Das Band darf NIEMALS rueckwaerts springen. Ein korrigierter Schaetzwert
// wird angehalten (angehalten=true), nicht zurueckgesetzt: ein schrumpfender
// Balken liest sich als Fehler, ein stehender nur als Nachdenken.
//
// Keine Icon-Komponente hier: der Icon-Chunk wiegt 576 KB roh, und dieses
// Bauteil steht ausgerechnet auf den langsamen Pfaden (Import, Export). Die
// drei Zustandsmarken sind deshalb reines CSS.
// ============================================================
import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { LANGSAM_MS } from "../../lib/ladephase";
import "./ladewerk.css";

export interface FortschrittSchritt {
  /** Stabile Kennung; fehlt sie, wird der Text als Schluessel benutzt. */
  id?: string;
  text: string;
  zustand?: "laeuft" | "fertig" | "fehler";
}

export interface FortschrittProps {
  /** Bestimmt: erledigte Einheiten. */
  wert?: number;
  /** Bestimmt: Gesamtzahl. Fehlt sie, laeuft das Band unbestimmt. */
  gesamt?: number;
  /** Einzahl der Einheit fuer die Schrittzeile: "Seite", "Datei", "Objekt". */
  einheit?: string;
  /** Partizip fuer die Schrittzeile: "gelesen", "hochgeladen", "geprueft". */
  taetigkeit?: string;
  /**
   * Unbestimmt: die Ereignisse. Reihenfolge ist Absicht — bei mehrstufigen
   * Vorgaengen gehoeren die UNZUVERLAESSIGEN Stufen ZUERST, damit die
   * Ungenauigkeit am Anfang liegt und der Schluss glatt durchlaeuft.
   */
  schritte?: readonly FortschrittSchritt[];
  /** Date.now() beim Start — nur damit gibt es ab 10 s eine grobe Restzeit. */
  beginn?: number;
  /** Beschriftung des ganzen Bandes fuer Hilfstechnik. */
  label?: string;
  /** Schaetzwert unbrauchbar: das Band bleibt stehen, statt zurueckzuspringen. */
  angehalten?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Grobe Restzeit, nie sekundengenau. Eine Sekundenanzeige, die zwischen
 * "noch 8 s" und "noch 41 s" springt, zerstoert mehr Vertrauen, als sie
 * Information bringt.
 */
function restzeitText(verstrichen: number, anteil: number): string | null {
  if (verstrichen < LANGSAM_MS || anteil <= 0.05 || anteil >= 1) return null;
  const rest = (verstrichen * (1 - anteil)) / anteil;
  if (rest < 45000) return "noch weniger als eine Minute";
  const minuten = Math.round(rest / 60000);
  return minuten <= 1 ? "noch etwa eine Minute" : `noch etwa ${minuten} Minuten`;
}

export function Fortschritt({
  wert,
  gesamt,
  einheit = "Schritt",
  taetigkeit = "erledigt",
  schritte,
  beginn,
  label,
  angehalten = false,
  className = "",
  style,
}: FortschrittProps): ReactElement {
  const bestimmt = typeof wert === "number" && typeof gesamt === "number" && gesamt > 0;
  const rohAnteil = bestimmt ? Math.min(1, Math.max(0, (wert ?? 0) / (gesamt ?? 1))) : 0;

  // Sperrklinke. Waehrend des Renderns gefuehrt, weil sie idempotent ist:
  // ein zweiter Lauf mit denselben Werten aendert das Maximum nicht.
  const gezeigtRef = useRef(0);
  const laufRef = useRef("");
  const lauf = `${gesamt ?? 0}|${beginn ?? 0}`;
  if (laufRef.current !== lauf) {
    // Neuer Vorgang — die Klinke faellt zurueck, sonst startet der zweite
    // Import optisch dort, wo der erste aufgehoert hat.
    laufRef.current = lauf;
    gezeigtRef.current = 0;
  }
  if (!angehalten) {
    const kurve = Math.pow(rohAnteil, 1.6);
    if (kurve > gezeigtRef.current) gezeigtRef.current = kurve;
  }
  const anteil = gezeigtRef.current;

  // Sekundenuhr nur fuer die Restzeit, und nur wenn es eine geben kann.
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!beginn || !bestimmt) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 1000);
    return () => clearInterval(uhr);
  }, [beginn, bestimmt]);

  const rest = beginn && bestimmt ? restzeitText(jetzt - beginn, rohAnteil) : null;
  const zeile = bestimmt ? `${einheit} ${wert} von ${gesamt} ${taetigkeit}` : null;

  return (
    <div className={`lwf ${className}`.trim()} style={style} role="group" aria-label={label}>
      <div
        className="lwf-bahn"
        data-art={bestimmt ? "bestimmt" : "unbestimmt"}
        data-angehalten={angehalten ? "true" : undefined}
        role="progressbar"
        aria-label={label}
        aria-valuemin={bestimmt ? 0 : undefined}
        aria-valuemax={bestimmt ? gesamt : undefined}
        aria-valuenow={bestimmt ? wert : undefined}
        aria-valuetext={zeile ?? undefined}
      >
        {/* Bei unbestimmt wandert derselbe Knoten, statt zu wachsen — eine
            Schicht weniger und kein zweiter Zustand im DOM. */}
        <span className="lwf-fuellung" style={bestimmt ? { width: `${(anteil * 100).toFixed(1)}%` } : undefined} />
      </div>

      {(zeile || rest) && (
        <p className="lwf-zeile" aria-live="polite">
          {zeile}
          {zeile && rest ? " · " : null}
          {rest ? <span className="lwf-rest">{rest}</span> : null}
        </p>
      )}

      {schritte && schritte.length > 0 && (
        // aria-relevant="additions": nur der NEUE Schritt wird angesagt, nicht
        // jedes Mal die ganze gewachsene Liste.
        <ol className="lwf-schritte" aria-live="polite" aria-relevant="additions">
          {schritte.map((s, i) => (
            <li key={s.id ?? `${i}-${s.text}`} className="lwf-schritt" data-zustand={s.zustand ?? "fertig"}>
              <span className="lwf-marke" aria-hidden="true" />
              {s.text}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
